import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import OverviewTab from "../components/dashboard/OverviewTab";
import BriefsTab from "../components/dashboard/BriefsTab";
import SlotsTab from "../components/dashboard/SlotsTab";
import AccountsTab from "../components/dashboard/AccountsTab";
import ProfileTab from "../components/dashboard/ProfileTab";
import AdminTab from "../components/dashboard/AdminTab";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  const isEmployee = user.role === "employee";
  const tabs = [
    { id: "overview", label: "Overview", testid: "tab-overview" },
    ...(isEmployee
      ? [{ id: "slots", label: "Shop Slots", testid: "tab-slots" }]
      : [{ id: "briefs", label: "Project Briefs", testid: "tab-enquiries" }]),
    { id: "accounts", label: "Accounts", testid: "tab-accounts" },
    { id: "profile", label: "Profile & Security", testid: "tab-profile" },
    ...(user.role === "admin" ? [{ id: "admin", label: "Admin", testid: "tab-admin" }] : []),
  ];

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out");
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-obsidian text-slate-50" data-testid="dashboard-page">
      <header className="border-b border-white/5 bg-panel/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <a href="/" aria-label="Back to site"><Logo size={30} /></a>
          <div className="flex items-center gap-3">
            <span
              data-testid="dashboard-unique-id"
              className="font-mono text-xs tracking-[0.2em] text-volt border border-volt/30 bg-volt/5 px-4 py-2"
            >
              {user.zid}
            </span>
            <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 border border-white/10 px-3 py-2">
              {user.role}
            </span>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-red-400 border border-white/10 hover:border-red-500/40 px-4 py-2 transition-colors duration-300"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
          <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-volt">
            {isEmployee ? "Employee Portal" : "Client Portal"}
          </p>
          <h1 className="mt-2 font-display font-bold tracking-tight text-3xl sm:text-4xl">
            Welcome, {user.name.split(" ")[0]}
          </h1>
        </motion.div>

        <div className="mt-8 flex flex-wrap gap-2 border-b border-white/5 pb-px">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={t.testid}
              onClick={() => setTab(t.id)}
              className={`font-mono text-[11px] uppercase tracking-[0.2em] px-5 py-3 border-b-2 -mb-px transition-colors duration-300 ${
                tab === t.id ? "border-volt text-volt" : "border-transparent text-slate-500 hover:text-slate-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            >
              {tab === "overview" && <OverviewTab user={user} goTo={setTab} />}
              {tab === "briefs" && <BriefsTab />}
              {tab === "slots" && <SlotsTab />}
              {tab === "accounts" && <AccountsTab />}
              {tab === "profile" && <ProfileTab user={user} />}
              {tab === "admin" && user.role === "admin" && <AdminTab />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
